import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MedicationForm from '../components/MedicationForm';
import {
  addMedication,
  deleteMedicationLogEntry,
  fetchMedications,
  logMedicationAction,
  updateMedication,
} from '../services/medicationService';
import {
  buildDailyLog,
  LOG_STATUS_META,
  partitionMedications,
  summarizeMedicationsForChatbot,
} from '../utils/medicationHelpers';

const VIEWS = [
  { key: 'current', label: 'Current' },
  { key: 'past', label: 'Past' },
  { key: 'daily', label: 'Daily Log' },
];

export default function MedicationTracker({ user, onOpenChat }) {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('current');
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [logDate, setLogDate] = useState(() => new Date().toISOString().slice(0, 10));
  const toastTimeoutRef = useRef(null);

  const username = user?.username || '';

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchMedications(username);
      setMedications(Array.isArray(data) ? data : []);
    } catch {
      setError('Could not load medications. Showing cached data when available.');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const { current, past } = useMemo(
    () => partitionMedications(medications),
    [medications]
  );

  const dailyLog = useMemo(() => buildDailyLog(medications, new Date(`${logDate}T00:00:00`)), [medications, logDate]);

  const handleAddMedication = async (payload) => {
    setSaving(true);
    try {
      const saved = await addMedication(username, payload);
      setMedications((prev) => [saved, ...prev]);
      setShowForm(false);
    } catch {
      setError('Failed to add medication. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (medicationId, status) => {
    const updated = await logMedicationAction(medicationId, status, new Date().toISOString());
    if (!updated) return;

    setMedications((prev) =>
      prev.map((m) => (String(m._id) === String(medicationId) ? updated : m))
    );

    const actionLabel = status === 'taken' ? 'Taken' : status === 'skipped' ? 'Skipped' : 'Missed';
    const statusTheme =
      status === 'taken'
        ? { color: '#338477', bg: '#e3f0ee', ring: '#338477' }
        : status === 'skipped'
        ? { color: '#78350f', bg: '#fef3c7', ring: '#f59e0b' }
        : { color: '#7f1d1d', bg: '#fee2e2', ring: '#ef4444' };

    setToast({
      message: `${actionLabel} saved to your daily medication log.`,
      ...statusTheme,
    });

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => setToast(null), 2200);
  };

  const handleStatusChange = async (medicationId, nextStatus) => {
    const updates = {
      status: nextStatus,
      endDate: nextStatus === 'past' ? new Date().toISOString().slice(0, 10) : null,
    };
    const updated = await updateMedication(medicationId, updates);
    if (!updated) return;

    setMedications((prev) =>
      prev.map((m) => (String(m._id) === String(medicationId) ? updated : m))
    );
  };

  const handleDeleteLogEntry = async (entry) => {
    const updated = await deleteMedicationLogEntry(entry.medicationId, entry);
    if (!updated) return;

    setMedications((prev) =>
      prev.map((m) => (String(m._id) === String(entry.medicationId) ? updated : m))
    );

    setToast({
      message: 'Entry removed from daily medication log.',
      color: '#0f172a',
      bg: '#e2e8f0',
      ring: '#64748b',
    });

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => setToast(null), 2000);
  };

  const handleChatWithMedicationContext = () => {
    const context = summarizeMedicationsForChatbot(medications);
    localStorage.setItem('medicationContext', context);
    if (onOpenChat) onOpenChat(context);
  };

  const currentDataset = view === 'current' ? current : view === 'past' ? past : dailyLog;

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', paddingBottom: '40px' }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.36)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1600,
            padding: '16px',
          }}
        >
          <div
            style={{
              width: 'min(92vw, 520px)',
              borderRadius: '20px',
              border: `3px solid ${toast.ring}`,
              background: toast.bg,
              color: toast.color,
              boxShadow: '0 22px 45px rgba(15, 23, 42, 0.34)',
              textAlign: 'center',
              padding: '28px 18px 24px',
              animation: 'medToastCardIn 240ms ease-out',
            }}
          >
            <div
              style={{
                width: '136px',
                height: '136px',
                margin: '0 auto 14px',
                borderRadius: '999px',
                border: `6px solid ${toast.ring}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '86px',
                lineHeight: 1,
                fontWeight: '800',
                color: toast.ring,
                background: 'white',
                animation: 'medCheckPop 620ms ease-out',
              }}
            >
              ✓
            </div>
            <div style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '0.2px' }}>Saved</div>
            <div style={{ marginTop: '8px', fontSize: '18px', fontWeight: 600 }}>{toast.message}</div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes medToastCardIn {
          0% { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes medCheckPop {
          0% { transform: scale(0.2) rotate(-16deg); opacity: 0; }
          55% { transform: scale(1.14) rotate(6deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', color: '#111827' }}>Medication Tracker</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
            {medications.length} medication record{medications.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          style={primaryBtn}
        >
          + Add Medication
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '14px', padding: '11px', borderRadius: '8px', background: '#FCEBEB', border: '1px solid #FECACA', color: '#991B1B', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', background: 'var(--page-bg-alt)', borderRadius: '10px', padding: '3px', marginBottom: '16px', gap: '2px' }}>
        {VIEWS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setView(tab.key)}
            style={{
              flex: 1,
              padding: '9px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              background: view === tab.key ? 'white' : 'transparent',
              color: view === tab.key ? 'var(--teal)' : '#64748b',
              fontWeight: view === tab.key ? '600' : '400',
              fontSize: '14px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'daily' && (
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '12px', color: '#475569', fontWeight: '600' }}>Filter by date:</label>
          <input
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            style={{
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '7px 9px',
              fontSize: '13px',
              color: '#0f172a',
              background: 'white',
            }}
          />
        </div>
      )}

      {loading ? (
        <div style={stateCardStyle}>Loading medications...</div>
      ) : currentDataset.length === 0 ? (
        <div style={stateCardStyle}>
          {view === 'daily'
            ? `No medication logs for ${new Date(`${logDate}T00:00:00`).toLocaleDateString()}.`
            : `No ${view} medications yet.`}
        </div>
      ) : view === 'daily' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {dailyLog.map((entry, idx) => {
            const meta = LOG_STATUS_META[entry.status] || LOG_STATUS_META.missed;
            return (
              <div key={`${entry.medicationId}-${idx}`} style={logCardStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {entry.medicationName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {entry.din ? `DIN ${entry.din} - ` : ''}{entry.dosage}
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                    {new Date(entry.timestamp).toLocaleString([], {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                <span style={{ ...statusChipStyle, background: meta.bg, color: meta.color }}>
                  {meta.label}
                </span>
                <button
                  type="button"
                  onClick={() => handleDeleteLogEntry(entry)}
                  style={deleteLogBtnStyle}
                >
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(view === 'current' ? current : past).map((medication) => (
            <div key={String(medication._id)} style={medicationCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>
                    {medication.productName || medication.medicationName}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                    {medication.din ? `DIN ${medication.din} - ` : ''}{medication.dosage} - {medication.frequency}
                  </p>
                </div>
                <span style={{ ...statusChipStyle, background: medication.status === 'past' ? '#e5e7eb' : 'var(--brand-primary-light)', color: medication.status === 'past' ? '#374151' : 'var(--teal)' }}>
                  {medication.status === 'past' ? 'Past' : 'Current'}
                </span>
              </div>

              <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={detailItemStyle}><strong>Start:</strong> {new Date(medication.startDate).toLocaleDateString()}</div>
                <div style={detailItemStyle}><strong>End:</strong> {medication.endDate ? new Date(medication.endDate).toLocaleDateString() : 'N/A'}</div>
                <div style={{ ...detailItemStyle, gridColumn: '1 / -1' }}><strong>Instructions:</strong> {medication.instructions || 'None'}</div>
              </div>

              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => handleAction(medication._id, 'taken')} style={{ ...actionBtnStyle, background: 'var(--brand-accent-light)', color: 'var(--brand-accent)' }}>
                  Mark Taken
                </button>
                <button type="button" onClick={() => handleAction(medication._id, 'skipped')} style={{ ...actionBtnStyle, background: '#FAEEDA', color: '#9A5A00' }}>
                  Mark Skipped
                </button>
                <button type="button" onClick={() => handleAction(medication._id, 'missed')} style={{ ...actionBtnStyle, background: '#FCEBEB', color: '#B42318' }}>
                  Mark Missed
                </button>
                {medication.status === 'current' ? (
                  <button type="button" onClick={() => handleStatusChange(medication._id, 'past')} style={ghostBtnStyle}>
                    Move to Past
                  </button>
                ) : (
                  <button type="button" onClick={() => handleStatusChange(medication._id, 'current')} style={ghostBtnStyle}>
                    Move to Current
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {medications.length > 0 && onOpenChat && (
        <div style={{ marginTop: '16px' }}>
          <button type="button" onClick={handleChatWithMedicationContext} style={ghostBtnWideStyle}>
            Discuss medication history with Reaxano AI
          </button>
        </div>
      )}

      {showForm && (
        <MedicationForm
          onClose={() => setShowForm(false)}
          onSave={handleAddMedication}
          saving={saving}
        />
      )}
    </div>
  );
}

const medicationCardStyle = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '14px',
};

const detailItemStyle = {
  fontSize: '12px',
  color: '#374151',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '8px',
};

const statusChipStyle = {
  fontSize: '11px',
  fontWeight: '600',
  padding: '4px 9px',
  borderRadius: '999px',
};

const actionBtnStyle = {
  border: 'none',
  borderRadius: '8px',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const ghostBtnStyle = {
  border: '1px solid #cbd5e1',
  background: 'white',
  color: '#334155',
  borderRadius: '8px',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const ghostBtnWideStyle = {
  width: '100%',
  border: '1px solid var(--teal)',
  background: 'white',
  color: 'var(--teal)',
  borderRadius: '10px',
  padding: '11px 12px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};

const primaryBtn = {
  padding: '10px 18px',
  background: 'var(--teal)',
  color: 'white',
  border: 'none',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const stateCardStyle = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '30px 14px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#64748b',
};

const logCardStyle = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const deleteLogBtnStyle = {
  border: '1px solid #fecaca',
  background: '#fff1f2',
  color: '#9f1239',
  borderRadius: '8px',
  padding: '7px 9px',
  fontSize: '12px',
  fontWeight: '700',
  cursor: 'pointer',
};
